/**
 * API Cache with Request Deduplication
 * 
 * Benefits:
 * - Prevents duplicate API calls when multiple users request same data
 * - Caches responses to reduce server load
 * - Deduplicates in-flight requests (if 100 users request same data, only 1 API call)
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class APICache {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, Promise<any>>();
  
  /**
   * Fetch data with caching and deduplication
   * 
   * @param key - Unique cache key
   * @param fetcher - Function that fetches the data
   * @param ttl - Time to live in milliseconds (default: 30 seconds)
   */
  async fetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = 30000
  ): Promise<T> {
    const now = Date.now();
    const cached = this.cache.get(key);
    
    // Return cached data if still fresh
    if (cached && (now - cached.timestamp) < ttl) {
      return cached.data;
    }
    
    // Request deduplication: if same request is already pending, wait for it
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }
    
    // Make new request
    const promise = fetcher()
      .then(data => {
        // Cache the result
        this.cache.set(key, { data, timestamp: now });
        return data;
      })
      .catch(error => {
        console.error(`❌ Fetch failed: ${key}`, error);
        throw error;
      })
      .finally(() => {
        // Remove from pending requests
        this.pendingRequests.delete(key);
      });
    
    this.pendingRequests.set(key, promise);
    return promise;
  }
  
  /**
   * Clear cache entries (all or by pattern)
   */
  clear(pattern?: string) {
    if (pattern) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let _cleared = 0; // Intentionally kept for potential future use
      const keys = Array.from(this.cache.keys());
      for (const key of keys) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          _cleared++;
        }
      }
      } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _count = this.cache.size;
      this.cache.clear();
      }
  }
  
  /**
   * Invalidate a specific cache entry
   */
  invalidate(key: string) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
      }
  }
  
  /**
   * Get cache statistics
   */
  getStats() {
    const keys = Array.from(this.cache.keys());
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      keys: keys,
    };
  }
}

// Global singleton instance
export const apiCache = new APICache();

// Utility function for creating cache keys
export function createCacheKey(parts: (string | number | undefined)[]): string {
  return parts.filter(Boolean).join('_');
}

